<template>
    <modal-3
        id_modal="modal_convenio_federal_detalhe"
        class_modal="modal-lg shadow"
        backdrop="static"
        v-on:opened="onOpened"
        v-on:closed="onClosed"
    >
        <template v-slot:header>
            <span
                id="titulo_modal_convenio_federal_detalhe"
                class="m-0 px-2 mr-auto text-muted font-weight-normal font-size-07 d-flex align-items-center"
            >
                <span v-if="convenio && convenio.id_convenio_portal">{{ convenio.id_convenio_portal }}</span>
            </span>
        </template>

        <!-- ============ LOADING ============ -->
        <div v-if="carregando" class="text-center py-5">
            <hollow-dots-spinner
                :animation-duration="1000"
                :dot-size="10"
                :dots-num="3"
                color="#6F787D"
                style="display: inline-block;"/>
            <div class="mt-2 small text-muted">Carregando convênio...</div>
        </div>

        <!-- ============ ERRO ============ -->
        <div v-else-if="erro" class="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
            <div>
                <i class="fas fa-exclamation-triangle mr-2"></i>
                {{ erro }}
            </div>
            <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                v-on:click="fetchDetalhe"
                aria-label="Tentar carregar novamente">
                <i class="fas fa-redo mr-1"></i> Tentar novamente
            </button>
        </div>

        <!-- ============ CONTEÚDO ============ -->
        <div v-else-if="convenio">

            <!-- Dados do convênio -->
            <section class="mb-3" aria-labelledby="cf-secao-dados">
                <h6 id="cf-secao-dados" class="text-uppercase small text-muted mb-2">Dados do convênio</h6>
                <div class="row">
                    <div class="col-md-6 mb-2">
                        <small class="text-muted d-block">Nº do convênio</small>
                        <span>{{ convenio.numero_convenio || '—' }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                        <small class="text-muted d-block">Situação</small>
                        <span class="badge" :class="badgeSituacaoClass(convenio.situacao)">
                            {{ convenio.situacao || 'N/I' }}
                        </span>
                    </div>

                    <div class="col-12 mb-2">
                        <small class="text-muted d-block">Objeto</small>
                        <span>{{ convenio.objeto || '—' }}</span>
                    </div>

                    <div class="col-md-6 mb-2">
                        <small class="text-muted d-block">Concedente (CNPJ)</small>
                        <span>{{ formatarCnpj(convenio.cnpj_concedente) }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                        <small class="text-muted d-block">Concedente (nome)</small>
                        <span>{{ convenio.nome_concedente || '—' }}</span>
                    </div>

                    <div class="col-md-6 mb-2">
                        <small class="text-muted d-block">Convenente (CNPJ)</small>
                        <span>{{ formatarCnpj(convenio.cnpj_convenente) }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                        <small class="text-muted d-block">Convenente (nome)</small>
                        <span>{{ convenio.nome_convenente || '—' }}</span>
                    </div>

                    <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Valor global</small>
                        <span>{{ formatarReal(convenio.valor_global) }}</span>
                    </div>
                    <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Valor repasse</small>
                        <span>{{ formatarReal(convenio.valor_repasse) }}</span>
                    </div>
                    <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Valor contrapartida</small>
                        <span>{{ formatarReal(convenio.valor_contrapartida) }}</span>
                    </div>

                    <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Data publicação</small>
                        <span>{{ formatarData(convenio.data_celebracao) }}</span>
                    </div>
                    <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Início vigência</small>
                        <span>{{ formatarData(convenio.data_inicio_vigencia) }}</span>
                    </div>
                    <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Fim vigência</small>
                        <span>{{ formatarData(convenio.data_fim_vigencia) }}</span>
                    </div>
                </div>
            </section>

            <!-- Anexos -->
            <section class="mb-3" aria-labelledby="cf-secao-anexos">
                <h6 id="cf-secao-anexos" class="text-uppercase small text-muted mb-2">Documentos anexos</h6>

                <div v-if="!anexos.length" class="alert alert-warning mb-2 py-2 px-3 small">
                    <i class="fas fa-info-circle mr-1"></i>
                    Nenhum anexo. Faça upload do primeiro PDF abaixo.
                </div>

                <ul v-else class="list-group list-group-flush mb-2">
                    <li
                        v-for="anexo in anexos"
                        :key="anexo.id_anexo"
                        class="list-group-item d-flex align-items-center justify-content-between px-2 py-2">
                        <div class="d-flex align-items-center" style="min-width: 0;">
                            <i class="far fa-file-pdf text-danger mr-2 font-size-1" aria-hidden="true"></i>
                            <a
                                :href="anexo.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-truncate"
                                :title="anexo.name_file"
                                :aria-label="'Abrir anexo ' + anexo.name_file + ' (nova aba)'">
                                {{ anexo.name_file }}
                            </a>
                            <small class="text-muted ml-2 flex-shrink-0">{{ formatarTamanho(anexo.size) }}</small>
                        </div>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-danger border-0 ml-2"
                            v-on:click="excluir(anexo)"
                            :aria-label="'Excluir anexo ' + anexo.name_file"
                            :title="'Excluir ' + anexo.name_file">
                            <i class="far fa-trash-alt"></i>
                        </button>
                    </li>
                </ul>

                <upload-files
                    key="upload_anexos_convenio_federal"
                    :request="{ post: '/anexos/upload' }"
                    name_file="arquivo_upload"
                    id_file="upload_anexos_convenio_federal"
                    :dados="dadosUpload"
                    :drop_file="true"
                    :edit_info="true"
                    :img_size_limit="104857600"
                    file_format="^.*\.pdf$"
                    v-on:success="onUploadSuccess"
                ></upload-files>

                <small class="text-muted d-block mt-1">Apenas PDF, até 100 MB por arquivo.</small>
            </section>

            <!-- Rodapé: timestamps -->
            <footer class="border-top pt-2 mt-2 small text-muted d-flex flex-wrap justify-content-between">
                <span>
                    Lista sincronizada em:
                    <strong>{{ formatarDataHora(convenio.lista_sincronizada_em) }}</strong>
                </span>
                <span>
                    Detalhe sincronizado em:
                    <strong>{{ formatarDataHora(convenio.detalhe_sincronizado_em) }}</strong>
                </span>
            </footer>
        </div>
    </modal-3>
</template>

<script>
export default {
    name: 'ConvenioFederalDetalheModal',
    props: {
        id: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            convenio: null,
            anexos: [],
            carregando: false,
            erro: null,
            csrfToken: (document.querySelector('meta[name="csrf-token"]')
                ? document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                : ''),
        };
    },
    computed: {
        dadosUpload() {
            return {
                _token: this.csrfToken,
                id_convenio_federal: this.id,
            };
        },
    },
    methods: {
        onOpened() {
            this.fetchDetalhe();
            this.focarBotaoUpload();
        },
        onClosed() {
            this.convenio = null;
            this.anexos = [];
            this.erro = null;
            // O id corrente vive no store e e sempre sobrescrito por
            // abrirDetalheConvenioFederal() ao reabrir, entao nao limpamos aqui:
            // zerar no `closed` causaria race se o usuario reabrisse durante a
            // animacao de fechamento (o reset apagaria o id ja setado).
        },
        focarBotaoUpload() {
            // Foco no botão de seleção do upload-files (acessibilidade).
            setTimeout(() => {
                const input = document.getElementById('upload_anexos_convenio_federal');
                if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            }, 300);
        },
        fetchDetalhe() {
            if (!this.id) {
                return;
            }
            this.carregando = true;
            this.erro = null;
            axios.get('/convenios-federais/' + this.id)
                .then((res) => {
                    const dados = res.data || {};
                    this.convenio = dados.convenio || null;
                    this.anexos = dados.anexos || [];
                })
                .catch((err) => {
                    this.convenio = null;
                    this.anexos = [];
                    this.erro = this.extrairMensagemErro(err)
                        || 'Não foi possível carregar o convênio.';
                })
                .finally(() => {
                    this.carregando = false;
                });
        },
        onUploadSuccess() {
            this.$noty.success('Anexo(s) enviado(s)!');
            this.fetchDetalhe();
            this.$emit('refresh');
        },
        excluir(anexo) {
            this.$swal.fire({
                title: 'Excluir anexo?',
                text: anexo.name_file,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Excluir',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#dc3545',
            }).then((res) => {
                if (!res.isConfirmed) {
                    return;
                }
                axios.delete('/convenios-federais/' + this.id + '/anexos/' + anexo.id_anexo)
                    .then(() => {
                        this.$noty.success('Anexo excluído.');
                        this.fetchDetalhe();
                        this.$emit('refresh');
                    })
                    .catch((err) => {
                        const msg = this.extrairMensagemErro(err) || 'Falha ao excluir o anexo.';
                        this.$noty.error(msg, { timeout: 4000 });
                    });
            });
        },
        badgeSituacaoClass(situacao) {
            const s = (situacao || '').toLowerCase();
            if (s.includes('execu')) return 'badge-info';
            if (s.includes('conclu') || s.includes('adimpl')) return 'badge-success';
            if (s.includes('prestaç') || s.includes('prestac')) return 'badge-primary';
            if (s.includes('suspens')) return 'badge-warning';
            if (s.includes('rescind') || s.includes('anula') || s.includes('inadimpl')) return 'badge-danger';
            return 'badge-secondary';
        },
        formatarCnpj(cnpj) {
            if (!cnpj) return '—';
            // Preserva letras (CNPJ alfanumerico, IN RFB 2229/2024).
            const limpo = String(cnpj).replace(/[^0-9A-Za-z]/g, '').toUpperCase();
            if (limpo.length !== 14) return cnpj;
            return limpo.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
        },
        formatarReal(v) {
            if (v === null || v === undefined || v === '') return '—';
            const num = Number(v);
            if (isNaN(num)) return '—';
            return 'R$ ' + new Intl.NumberFormat('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(num);
        },
        formatarData(data) {
            if (!data) return '—';
            const partes = String(data).substring(0, 10).match(/(\d+)/g);
            if (!partes || partes.length < 3) return data;
            return partes[2] + '/' + partes[1] + '/' + partes[0];
        },
        formatarDataHora(data) {
            if (!data) return '—';
            const str = String(data);
            const datePart = str.substring(0, 10);
            const timePart = str.length > 10 ? str.substring(11, 19) : '';
            const partes = datePart.match(/(\d+)/g);
            if (!partes || partes.length < 3) return data;
            const formatada = partes[2] + '/' + partes[1] + '/' + partes[0];
            return timePart ? (formatada + ' ' + timePart) : formatada;
        },
        formatarTamanho(bytes) {
            if (bytes === null || bytes === undefined || bytes === '') return '';
            const num = Number(bytes);
            if (isNaN(num) || num <= 0) return '';
            const kb = num / 1024;
            if (kb < 1024) return kb.toFixed(1) + ' KB';
            return (kb / 1024).toFixed(2) + ' MB';
        },
        extrairMensagemErro(err) {
            if (!err) return null;
            if (err.response && err.response.data) {
                if (typeof err.response.data === 'string') return err.response.data;
                if (err.response.data.msg) return err.response.data.msg;
                if (err.response.data.message) return err.response.data.message;
            }
            return err.message || null;
        },
    },
};
</script>
